import React, { useEffect, useState } from "react";
import { Plus, HelpCircle, CheckCircle2, Trash2, ArchiveRestore, Archive as ArchiveIco, Share2, ChevronRight, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AttachmentPreview from "../../components/AttachmentPreview";
import MultiUploader from "../../components/MultiUploader";
import Loading from "../../components/Loading";

function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return ""; } }

function AskComposer({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [attIds, setAttIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/community-qa", { title, body, attachment_ids: attIds });
      onCreated(r.data);
      setTitle(""); setBody(""); setAttIds([]);
      setOpen(false);
      toast.success("QUESTION TRANSMITTED");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SUBMIT FAILED");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="sat-btn sat-btn-solid" data-testid="qa-new-btn">
        <Plus size={12} /> ASK A QUESTION
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-4 space-y-3" data-testid="qa-composer">
      <div className="sat-label sat-cyan">NEW QUESTION</div>
      <input
        data-testid="qa-title" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="TITLE — Ask something specific" maxLength={200} required minLength={4}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm"
      />
      <textarea
        data-testid="qa-body" value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="Add context (optional)" rows={5} maxLength={4000}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none"
      />
      <MultiUploader ids={attIds} onChange={setAttIds} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="sat-btn !py-2 !px-3">CANCEL</button>
        <button type="submit" disabled={busy || !title.trim()} data-testid="qa-submit"
          className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
          {busy ? "TRANSMITTING…" : "SEND QUESTION"}
        </button>
      </div>
    </form>
  );
}

function AnswerComposer({ qid, onPosted, disabled }) {
  const [body, setBody] = useState("");
  const [attIds, setAttIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || busy || disabled) return;
    setBusy(true);
    try {
      const r = await api.post(`/community-qa/${qid}/answers`, { body, attachment_ids: attIds });
      onPosted(r.data);
      setBody(""); setAttIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ANSWER FAILED");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2 border-t border-[color:var(--sat-border)] pt-3 mt-3">
      <textarea
        data-testid={`answer-body-${qid}`}
        value={body} onChange={(e) => setBody(e.target.value)}
        placeholder={disabled ? "This question is archived — new answers disabled" : "Share what you know…"}
        rows={3} maxLength={4000} disabled={disabled}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none disabled:opacity-50"
      />
      <MultiUploader ids={attIds} onChange={setAttIds} disabled={disabled} />
      <div className="flex justify-end">
        <button type="submit" disabled={busy || !body.trim() || disabled} data-testid={`answer-submit-${qid}`}
          className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
          <Send size={11} /> {busy ? "POSTING…" : "POST ANSWER"}
        </button>
      </div>
    </form>
  );
}

function QuestionThread({ q, isAdmin, currentUser, onChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/community-qa/${q.id}/answers`);
      setAnswers(r.data.results || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && answers.length === 0) load();
  };

  const doApprove = async () => {
    try {
      const r = await api.put(`/community-qa/${q.id}/approve?approved=${!q.approved}`);
      onChange({ ...q, approved: r.data.approved });
      toast.success(r.data.approved ? "APPROVED" : "APPROVAL REVOKED");
    } catch (err) { toast.error(err?.response?.data?.detail || "OPERATION FAILED"); }
  };
  const doArchive = async () => {
    try {
      const r = await api.put(`/community-qa/${q.id}/archive?archived=${!q.archived}`);
      onChange({ ...q, archived: r.data.archived });
      toast.success(r.data.archived ? "ARCHIVED" : "RESTORED");
    } catch (err) { toast.error(err?.response?.data?.detail || "OPERATION FAILED"); }
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this question and all its answers permanently?")) return;
    try {
      await api.delete(`/community-qa/${q.id}`);
      onDelete(q.id);
      toast.success("QUESTION DELETED");
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };
  const doShare = async () => {
    const url = `${window.location.origin}/community#qa/${q.id}`;
    try {
      if (navigator.share) await navigator.share({ title: q.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("LINK COPIED"); }
    } catch { /* user cancelled */ }
  };

  const canDeleteAnswer = (a) => currentUser && (a.author_id === currentUser.id || isAdmin);
  const deleteAnswer = async (aid) => {
    if (!window.confirm("Delete this answer?")) return;
    try {
      await api.delete(`/community-qa/${q.id}/answers/${aid}`);
      setAnswers((cur) => cur.filter(x => x.id !== aid));
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };

  return (
    <article className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4" data-testid={`qa-card-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="sat-label sat-cyan flex items-center gap-2 flex-wrap">
            <HelpCircle size={12} /> QUESTION · @{q.author_callsign}
            {q.approved && (
              <span className="text-[9px] px-1.5 py-0.5 border border-[color:var(--sat-cyan)] sat-cyan inline-flex items-center gap-1" data-testid={`qa-approved-${q.id}`}>
                <CheckCircle2 size={9} /> APPROVED
              </span>
            )}
            {q.archived && (
              <span className="text-[9px] px-1.5 py-0.5 border border-[color:var(--sat-live)] text-[color:var(--sat-live)] inline-flex items-center gap-1" data-testid={`qa-archived-${q.id}`}>
                <ArchiveIco size={9} /> ARCHIVED
              </span>
            )}
          </div>
          <button
            onClick={toggle}
            className="text-left font-display font-bold text-lg mt-1 hover:sat-cyan transition-colors flex items-center gap-2 w-full"
            data-testid={`qa-toggle-${q.id}`}
          >
            <span className="flex-1">{q.title}</span>
            <ChevronRight size={16} className={`shrink-0 transition-transform ${expanded ? "rotate-90 sat-cyan" : ""}`} />
          </button>
          <div className="sat-label mt-1 text-[color:var(--sat-text-dim)] flex items-center gap-2 flex-wrap">
            <span>{fmtTime(q.created_at)}</span>
            <span>·</span>
            <span>{q.answer_count} {q.answer_count === 1 ? "ANSWER" : "ANSWERS"}</span>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={doShare} className="sat-btn !py-1 !px-2" title="Share" data-testid={`qa-share-${q.id}`}>
            <Share2 size={11} />
          </button>
          {isAdmin && (
            <>
              <button onClick={doApprove} className={`sat-btn !py-1 !px-2 ${q.approved ? "sat-btn-solid" : ""}`}
                title={q.approved ? "Un-approve" : "Approve"} data-testid={`qa-approve-${q.id}`}>
                <ShieldCheck size={11} />
              </button>
              <button onClick={doArchive} className={`sat-btn !py-1 !px-2 ${q.archived ? "sat-btn-solid" : ""}`}
                title={q.archived ? "Unarchive" : "Archive"} data-testid={`qa-archive-${q.id}`}>
                {q.archived ? <ArchiveRestore size={11} /> : <ArchiveIco size={11} />}
              </button>
              <button onClick={doDelete} className="sat-btn sat-btn-danger !py-1 !px-2"
                title="Delete" data-testid={`qa-delete-${q.id}`}>
                <Trash2 size={11} />
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          {q.body && <div className="text-sm whitespace-pre-wrap leading-relaxed">{q.body}</div>}
          {q.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {q.attachments.map(a => <AttachmentPreview key={a.id} attachment={a} compact />)}
            </div>
          )}

          <div className="sat-label sat-cyan pt-2 border-t border-[color:var(--sat-border)]">
            ANSWERS · {answers.length || q.answer_count}
          </div>
          {loading && <div className="sat-label text-[color:var(--sat-text-dim)]">LOADING…</div>}
          {!loading && answers.length === 0 && (
            <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid={`qa-answers-empty-${q.id}`}>
              NO ANSWERS YET — BE THE FIRST.
            </div>
          )}
          <ul className="space-y-3" data-testid={`qa-answers-${q.id}`}>
            {answers.map(a => (
              <li key={a.id} className="border-l-2 border-[color:var(--sat-border)] pl-3 py-2" data-testid={`qa-answer-${a.id}`}>
                <div className="sat-label sat-cyan flex items-center gap-2">
                  @{a.author_callsign} · {fmtTime(a.created_at)}
                  {canDeleteAnswer(a) && (
                    <button onClick={() => deleteAnswer(a.id)} className="ml-2 hover:text-red-400"
                      data-testid={`qa-answer-delete-${a.id}`} title="Delete answer">
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap mt-1">{a.body}</div>
                {a.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {a.attachments.map(x => <AttachmentPreview key={x.id} attachment={x} compact />)}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {currentUser ? (
            <AnswerComposer qid={q.id} disabled={q.archived}
              onPosted={(a) => { setAnswers((cur) => [...cur, a]); onChange({ ...q, answer_count: q.answer_count + 1 }); }} />
          ) : (
            <div className="sat-label text-[color:var(--sat-text-dim)]">LOG IN TO ANSWER.</div>
          )}
        </div>
      )}
    </article>
  );
}

export default function QATab() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [view, setView] = useState("all"); // all | featured
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = async (v = view) => {
    setLoading(true);
    try {
      const r = await api.get(`/community-qa?state=${v}`);
      setRows(r.data.results || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "LOAD FAILED"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(view); /* eslint-disable-next-line */ }, [view]);

  const onChange = (updated) => setRows(cur => cur.map(x => x.id === updated.id ? updated : x));
  const onDelete = (id) => setRows(cur => cur.filter(x => x.id !== id));

  return (
    <div data-tab-key="qa" data-tab-content="1" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          {[["all","ALL QUESTIONS"],["featured","FEATURED"]].map(([k,label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`qa-view-${k}`}
              className={`sat-btn !py-2 !px-3 ${view === k ? "sat-btn-solid" : ""}`}>
              {label}
            </button>
          ))}
        </div>
        {user && <AskComposer onCreated={(q) => setRows(cur => [q, ...cur])} />}
      </div>
      {loading && <Loading message="LOADING QUESTIONS" />}
      {!loading && rows.length === 0 && (
        <div className="sat-label text-center py-10" data-testid="qa-empty">
          NO QUESTIONS {view === "featured" ? "FEATURED YET" : "YET"} — {user ? "TAP ASK A QUESTION." : "LOG IN TO ASK ONE."}
        </div>
      )}
      <div className="space-y-3">
        {rows.map(q => (
          <QuestionThread
            key={q.id} q={q} isAdmin={isAdmin} currentUser={user}
            onChange={onChange} onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}
